import React, { useState, useEffect } from 'react';
import { X, Gift } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

export const NewsletterPopup = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    // Check if popup was already shown
    const popupShown = sessionStorage.getItem('newsletter_popup_shown');
    if (popupShown) return;

    // Show popup after 10 seconds
    const timer = setTimeout(() => {
      setIsOpen(true);
      sessionStorage.setItem('newsletter_popup_shown', 'true');
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  // Exit intent detection
  useEffect(() => {
    const popupShown = sessionStorage.getItem('newsletter_popup_shown');
    if (popupShown) return;

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0) {
        setIsOpen(true);
        sessionStorage.setItem('newsletter_popup_shown', 'true');
      }
    };

    document.addEventListener('mouseleave', handleMouseLeave);
    return () => document.removeEventListener('mouseleave', handleMouseLeave);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (email) {
      setIsSubmitted(true);
      setTimeout(() => {
        setIsOpen(false);
      }, 2000);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-md p-0 overflow-hidden" data-testid="newsletter-popup">
        <div className="relative">
          {/* Background Pattern */}
          <div className="absolute inset-0 bg-gradient-to-br from-brand-orange to-brand-orange-hover opacity-10" />
          
          <div className="relative p-8">
            {!isSubmitted ? (
              <>
                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-brand-orange-light rounded-full flex items-center justify-center mx-auto mb-4">
                    <Gift className="w-8 h-8 text-brand-orange" />
                  </div>
                  <DialogHeader>
                    <DialogTitle className="font-heading text-2xl text-gray-800">
                      Get 10% Off Your First Order!
                    </DialogTitle>
                  </DialogHeader>
                  <p className="text-gray-600 mt-2">
                    Join our newsletter for exclusive deals, pet care tips, and new arrivals.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full rounded-xl"
                    required
                    data-testid="newsletter-email-input"
                  />
                  <Button type="submit" className="w-full btn-primary" data-testid="newsletter-submit-btn">
                    Get My 10% Off
                  </Button>
                </form>

                <p className="text-xs text-gray-500 text-center mt-4">
                  By subscribing, you agree to our Privacy Policy. Unsubscribe anytime.
                </p>
              </>
            ) : (
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center py-8"
              >
                <div className="w-16 h-16 bg-brand-green rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="font-heading text-xl text-gray-800 mb-2">You're In!</h3>
                <p className="text-gray-600">Check your email for your 10% discount code.</p>
              </motion.div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
